import {createSlice, createSelector} from '@reduxjs/toolkit'

const initialState = {
  // Product definitions
  productGroups: [],
  isDataLoaded: false,
  error: null,

  // Location state
  selectedLocationIds: [], // locations selected in the dropdown
  selectionsByLocation: {},
  // {
  //   [locationId]: {
  //     locationId: string,
  //     locationName: string,
  //     selectedProducts: {
  //       [productId]: {
  //         productId: string,
  //         name: string,
  //         price: number,
  //         type: 'software' | 'addon',
  //         billingFrequency: 'monthly' | 'one-time',
  //         configuredOptions: {},
  //       }
  //     }
  //   }
  // }
}

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    // Catalog data
    setProductGroups(state, action) {
      state.productGroups = action.payload
      state.isDataLoaded = true
    },
    setCatalogError(state, action) {
      state.error = action.payload
    },

    // Location init — called once after AccountInfo saves
    // payload: Array<{ locationId, locationName, address?, area?, isParent? }>
    initLocations(state, action) {
      const locations = action.payload
      state.selectionsByLocation = {}
      state.selectedLocationIds = []

      locations.forEach(loc => {
        const preSelected = (loc.preSelectedPlanCodes ?? []).reduce(
          (acc, code) => {
            acc[code] = {productId: code}
            return acc
          },
          {},
        )
        state.selectionsByLocation[loc.locationId] = {
          locationId: loc.locationId,
          locationName: loc.locationName,
          address: loc.address ?? '',
          area: loc.area ?? '',
          isParent: loc.isParent ?? false,
          selectedProducts: preSelected,
        }
      })

      // Pre-select the first location so the dropdown isn't blank on mount?????? =>>(Refactor to have the child location as selected for which createorder has been clicked )
      // if (locations[0]) {
      //   state.selectedLocationIds = [locations[0].locationId]
      // }
    },

    // Add a location chip to the dropdown selection
    // payload: locationId string
    selectLocation(state, action) {
      const locationId = action.payload
      if (!state.selectedLocationIds.includes(locationId)) {
        state.selectedLocationIds.push(locationId)
      }
    },

    // Remove a location chip from the dropdown selection
    // payload: locationId string
    deselectLocation(state, action) {
      state.selectedLocationIds = state.selectedLocationIds.filter(
        id => id !== action.payload,
      )
    },

    // For "Select All" / "Clear All" in the dropdown
    // payload: locationId[] | []
    setSelectedLocations(state, action) {
      state.selectedLocationIds = action.payload
    },

    // Product selection
    // Applies to ALL currently selected locations.
    // Intent is derived from the first selected location:
    //   · product already there → remove from all selected
    //   · product not there     → add to all selected
    // payload: { product: { productId, name, price, type, billingFrequency, ... } } ??????
    toggleProductForSelectedLocations(state, action) {
      const {product} = action.payload
      const selectedIds = state.selectedLocationIds
      if (!selectedIds.length) return

      const firstLoc = state.selectionsByLocation[selectedIds[0]]
      const isCurrentlySelected =
        !!firstLoc?.selectedProducts[product.productId]

      selectedIds.forEach(locationId => {
        const loc = state.selectionsByLocation[locationId]
        if (!loc) return

        if (isCurrentlySelected) {
          delete loc.selectedProducts[product.productId]
        } else {
          loc.selectedProducts[product.productId] = {
            productId: product.productId,
            name: product.name,
            price: product.price,
            type: product.type,
            billingFrequency: product.billingFrequency ?? 'monthly',
            configuredOptions: product.configuredOptions ?? {},
          }
        }
      })
    },

    // Add product to all selected locations.
    // Mirrors kp11CartSlice.selectItem exclusion logic:
    //   · removes sibling plans from the same group (groupPlanCodes)
    //   · removes any product listed in the new product's excludes array
    // payload: { product: { productId, name, price, type, billingFrequency, configuredOptions, excludes? }, groupPlanCodes?: string[] }
    addProductToSelectedLocations(state, action) {
      const {product, groupPlanCodes = []} = action.payload
      const selectedIds = state.selectedLocationIds
      if (!selectedIds.length) return

      selectedIds.forEach(locationId => {
        const loc = state.selectionsByLocation[locationId]
        if (!loc) return

        // Remove sibling plans from the same group
        groupPlanCodes.forEach(code => {
          if (code !== product.productId) delete loc.selectedProducts[code]
        })

        // Remove products excluded by the incoming product
        product.excludes?.forEach(excludedCode => {
          delete loc.selectedProducts[excludedCode]
        })

        loc.selectedProducts[product.productId] = {
          productId: product.productId,
          name: product.name,
          price: product.price,
          type: product.type,
          billingFrequency: product.billingFrequency ?? 'monthly',
          configuredOptions: product.configuredOptions ?? {},
        }
      })
    },

    // Configured options
    // payload: { locationId, productId, configuredOptions }
    updateProductOptions(state, action) {
      const {locationId, productId, configuredOptions} = action.payload
      const product =
        state.selectionsByLocation[locationId]?.selectedProducts[productId]
      if (!product) return
      product.configuredOptions = configuredOptions
    },

    // Cleanup
    clearLocationSelections(state, action) {
      const loc = state.selectionsByLocation[action.payload]
      if (loc) loc.selectedProducts = {}
    },

    resetCatalog() {
      return initialState
    },
  },
})

export const {
  setProductGroups,
  setCatalogError,
  initLocations,
  selectLocation,
  deselectLocation,
  setSelectedLocations,
  toggleProductForSelectedLocations,
  addProductToSelectedLocations,
  updateProductOptions,
  clearLocationSelections,
  resetCatalog,
} = catalogSlice.actions

// Base selectors

export const selectProductGroups = state => state.catalog.productGroups
export const selectIsCatalogDataLoaded = state => state.catalog.isDataLoaded
export const selectCatalogError = state => state.catalog.error
export const selectSelectedLocationIds = state =>
  state.catalog.selectedLocationIds
export const selectSelectionsByLocation = state =>
  state.catalog.selectionsByLocation

// Catalog view selectors

export const selectSoftwareGroups = state =>
  state.catalog.productGroups.filter(g => g.prod_type === 'Center')

export const selectAddonGroups = state =>
  state.catalog.productGroups.filter(g => g.prod_type === 'App')

export const selectAllLocations = createSelector(
  selectSelectionsByLocation,
  selections => Object.values(selections),
)

export const selectSelectedLocations = createSelector(
  selectSelectionsByLocation,
  selectSelectedLocationIds,
  (selections, ids) => ids.map(id => selections[id]).filter(Boolean),
)

// 'checked'       → product is in ALL selected locations
// 'indeterminate' → product is in SOME selected locations
// 'unchecked'     → product is in NO selected locations
// Used to drive checkbox appearance in the catalog grid
export const selectProductCheckState = createSelector(
  selectSelectionsByLocation,
  selectSelectedLocationIds,
  (_, productId) => productId,
  (selections, selectedIds, productId) => {
    if (!selectedIds.length) return 'unchecked'

    const checkedCount = selectedIds.filter(
      id => !!selections[id]?.selectedProducts[productId],
    ).length

    if (checkedCount === 0) return 'unchecked'
    if (checkedCount === selectedIds.length) return 'checked'
    return 'indeterminate'
  },
)

// Order summary selectors

export const selectOrderSummaryLocations = createSelector(
  selectSelectionsByLocation,
  selections =>
    Object.values(selections).map(loc => {
      const allProducts = Object.values(loc.selectedProducts)
      const softwareItems = allProducts.filter(p => p.type === 'software')
      const addonItems = allProducts.filter(p => p.type === 'addon')
      const total = allProducts.reduce((sum, p) => sum + p.price, 0)

      return {
        locationId: loc.locationId,
        locationName: loc.locationName,
        softwareItems,
        addonItems,
        itemCount: allProducts.length,
        total,
      }
    }),
)

export const selectOrderTotal = createSelector(
  selectOrderSummaryLocations,
  locations => locations.reduce((sum, loc) => sum + loc.total, 0),
)

export const selectOrderTotalItemCount = createSelector(
  selectOrderSummaryLocations,
  locations => locations.reduce((sum, loc) => sum + loc.itemCount, 0),
)

export default catalogSlice.reducer
